import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../api";
import { downloadBlob } from "./shared";

const EMPTY_HEADER = {
  title: "",
  destination: "",
  start_date: "",
  end_date: "",
  purpose: "",
  bank_info: "",
  payer_id: "",
  payment_reference: "",
  payment_date: "",
};

const headerFromDoc = (d) =>
  Object.fromEntries(Object.keys(EMPTY_HEADER).map((k) => [k, d[k] || ""]));

const validateHeader = (h) => {
  if (!h.title.trim()) return "Informe o título do relatório.";
  if (!h.payer_id) return "Selecione quem fez o pagamento.";
  if (h.payment_reference.trim().length < 2) return "Informe a que se refere o pagamento.";
  if (!h.payment_date) return "Informe quando o pagamento foi feito.";
  return null;
};

const apiError = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));

// State + actions for the expense detail page (create, edit, submit, decide).
export function useExpenseDetail(routeId, backTo) {
  const navigate = useNavigate();
  const isNew = !routeId || routeId === "novo";
  const [id, setId] = useState(isNew ? null : routeId);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [header, setHeader] = useState(EMPTY_HEADER);
  const [payers, setPayers] = useState([]);

  useEffect(() => {
    api
      .get("/portal/despesas/pagadores")
      .then((res) => setPayers(res.data))
      .catch(() => setPayers([]));
  }, []);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await api.get(`/portal/despesas/${id}`);
      setDoc(res.data);
      setHeader(headerFromDoc(res.data));
    } catch (e) {
      apiError(e);
      navigate(backTo);
    } finally {
      setLoading(false);
    }
  }, [id, navigate, backTo]);

  useEffect(() => {
    load();
  }, [load]);

  const saveHeader = async () => {
    const err = validateHeader(header);
    if (err) return toast.error(err);
    setSaving(true);
    try {
      const res = isNew && !id
        ? await api.post("/portal/despesas", header)
        : await api.patch(`/portal/despesas/${id}`, header);
      setDoc(res.data);
      if (!id) {
        setId(res.data.id);
        toast.success("Rascunho criado.");
        navigate(`${backTo}/${res.data.id}`, { replace: true });
      } else {
        toast.success("Alterações salvas.");
      }
    } catch (e) {
      apiError(e);
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    if (!window.confirm("Enviar para aprovação? Após envio o relatório fica travado.")) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/portal/despesas/${id}/submit`);
      setDoc(res.data);
      toast.success("Enviado para aprovação.");
    } catch (e) {
      apiError(e);
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancelar este relatório?")) return;
    try {
      const res = await api.post(`/portal/despesas/${id}/cancel`);
      setDoc(res.data);
      toast.success("Relatório cancelado.");
    } catch (e) {
      apiError(e);
    }
  };

  // Errors propagate to DecisionModal, which shows them.
  const decide = async (action, text) => {
    const isApprove = action === "approve";
    const res = await api.post(
      `/portal/despesas/${id}/${action}`,
      isApprove ? { comment: text || null } : { reason: text }
    );
    setDoc(res.data);
    if (isApprove) toast.success("Aprovação registrada.");
    else toast.info("Relatório rejeitado.");
  };

  const removeItem = async (itemId) => {
    if (!window.confirm("Remover este item?")) return;
    try {
      const res = await api.delete(`/portal/despesas/${id}/itens/${itemId}`);
      setDoc(res.data);
    } catch (e) {
      apiError(e);
    }
  };

  const downloadReceipt = async (item) => {
    try {
      const res = await api.get(`/portal/despesas/${id}/comprovante/${item.id}`, { responseType: "blob" });
      downloadBlob(res.data, item.receipt_filename || "comprovante");
    } catch (e) {
      apiError(e);
    }
  };

  return {
    id, isNew, doc, setDoc, loading, saving, submitting, header, setHeader, payers,
    saveHeader, submit, cancel, decide, removeItem, downloadReceipt,
  };
}
